import React, { useMemo } from 'react';
import { Typography, Box, Card, CardHeader, CardContent, Button, Container, Grid, Paper, Chip, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  generateRainbowColors, 
  getAgeDemos, 
  getGenderDemos, 
  higherValueAge, 
  higherValueGender 
} from '../../../../../Utils/constants';
import { 
  Bar, 
  BarChart, 
  Cell, 
  LabelList, 
  Tooltip, 
  XAxis, 
  YAxis, 
  ResponsiveContainer, 
  Pie, 
  PieChart 
} from 'recharts';

// Modern gradient button with hover effects
const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '12px',
  padding: '12px 28px',
  color: '#fff',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
  transition: 'all 0.3s ease',
  border: 'none',
  '&:hover': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
  },
}));

// Modern glass-morphism card design
const ModernCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  },
}));

// Styled card header with modern typography
const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  padding: '24px 24px 16px',
  '& .MuiCardHeader-title': {
    fontSize: '1.25rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #667eea, #764ba2)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
}));

// Modern card content with better spacing
const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: '0 24px 24px',
  '&:last-child': {
    paddingBottom: '24px',
  },
}));

// Metric display component with modern styling
const MetricBox = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
  border: '1px solid rgba(102, 126, 234, 0.2)',
  marginBottom: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15))',
  },
}));

// Modern container with better spacing
const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  minHeight: '100vh',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

// Modern section title
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 800,
  background: 'linear-gradient(135deg, #667eea, #764ba2)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

// Chart container with modern styling
const ChartContainer = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.6)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// Modern tooltip styling
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper 
        sx={{
          p: 2,
          borderRadius: '12px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Typography 
            key={index} 
            variant="body2" 
            sx={{ color: entry.color }}
          >
            {`${entry.name}: ${typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}`}
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
};

const CampaignProjectionsPanel = ({ campaignDetails }) => {
  const creatorsReachProjections = useMemo(() => {
    if (!campaignDetails) return [];

    return campaignDetails.creators.map((creator) => {
      const relevantDemo = creator.creator_details?.public?.find(
        (demo) => demo.platform?.name === creator.promotionPlatform
      );
      if (!relevantDemo) return null;

      const price = creator.agencyRate ? creator.agencyRate : creator.price;

      return {
        id: creator.id,
        age_data: getAgeDemos(relevantDemo.audience_data),
        gender_data: getGenderDemos(relevantDemo.audience_data),
        country_data: relevantDemo.country_data,
        average_views: relevantDemo.average_views * 0.33,
        cpm: (price / relevantDemo.average_views) * 1000 * 0.33,
      };
    }).filter(Boolean);
  }, [campaignDetails]);

  const totalProjectedData = useMemo(() => {
    const accumDetails = creatorsReachProjections.reduce(
      (campReach, reach) => {
        campReach.age_data = reach.age_data.reduce((curr_data, entry) => {
          const { age_range, value } = entry;
          const existingEntry = curr_data.find(item => item.age_range === age_range);

          if (existingEntry) {
            existingEntry.value += value;
          } else {
            const newEntry = { age_range, value: value }
            curr_data.push(newEntry);
          }
          return curr_data;
        }, campReach.age_data);

        campReach.gender_data = reach.gender_data.reduce((curr_data, entry) => {
          const { gender, value } = entry;
          const existingEntry = curr_data.find(item => item.gender === gender);

          if (existingEntry) {
            existingEntry.value += value;
          } else {
            const newEntry = { gender, value: value }
            curr_data.push(newEntry);
          }
          return curr_data;
        }, campReach.gender_data);

        campReach.country_data = reach.country_data.reduce((curr_data, entry) => {
          const { code, value } = entry;
          const existingEntry = curr_data.find(item => item.code === code);

          if (existingEntry) {
            existingEntry.value += value;
          } else {
            const newEntry = { code, value: value }
            curr_data.push(newEntry);
          }
          return curr_data;
        }, campReach.country_data);

        campReach.average_views += reach.average_views;
        campReach.average_cpm += reach.cpm;
        return campReach;
      }, {
        age_data: [],
        gender_data: [],
        country_data: [],
        average_views: 0,
        average_cpm: 0,
        projected_top_audience: 0,
        top_audience: 'N/A',
        projected_top_gender: 0,
        top_gender: 'N/A',
      }
    );

    accumDetails.age_data = accumDetails.age_data.map((entry) => ({ ...entry, value: entry.value / creatorsReachProjections.length }));
    accumDetails.gender_data = accumDetails.gender_data.map((entry) => ({ ...entry, value: entry.value / creatorsReachProjections.length }));
    accumDetails.country_data = accumDetails.country_data.map((entry) => ({ ...entry, value: entry.value / creatorsReachProjections.length })).filter((entry) => (entry.value > 0.9));
    const totalCountry = accumDetails.country_data.reduce((prevValue, entry) => (prevValue + entry.value), 0);
    accumDetails.country_data.push({ code: 'Others', value: (100 - totalCountry) });

    const highestAge = higherValueAge(accumDetails.age_data);
    const highestGender = higherValueGender(accumDetails.gender_data);

    accumDetails.top_audience = highestAge.age_range;
    accumDetails.projected_top_audience = (0.01 * highestAge.value) * accumDetails.average_views;

    accumDetails.top_gender = highestGender.gender;
    accumDetails.projected_top_gender = (0.01 * highestGender.value) * accumDetails.average_views;

    return accumDetails;
  }, [creatorsReachProjections]);

  // Modern color palettes
  const modernColors = [
    '#667eea', '#764ba2', '#f093fb', '#f5576c',
    '#4facfe', '#00f2fe', '#43e97b', '#38f9d7',
    '#ffecd2', '#fcb69f', '#ff8a80', '#ff5722'
  ];

  const genderColors = ['#667eea', '#f093fb', '#43e97b'];

  return (
    <StyledContainer maxWidth="xl">
      <SectionTitle variant="h2">
        Campaign Projections
      </SectionTitle>
      
      <Grid container spacing={4}>
        {/* Overview Card */}
        <Grid item xs={12} lg={6}>
          <ModernCard elevation={0}>
            <StyledCardHeader title="📊 Campaign Overview" />
            <StyledCardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MetricBox>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea', mb: 1 }}>
                      {totalProjectedData.average_views.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                      Projected Total Reach
                    </Typography>
                  </MetricBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MetricBox>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#764ba2', mb: 1 }}>
                      {Math.round(totalProjectedData.average_views / creatorsReachProjections.length).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                      Average Reach per Post
                    </Typography>
                  </MetricBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MetricBox>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f093fb', mb: 1 }}>
                      {campaignDetails.creators.length.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                      Creators in Campaign
                    </Typography>
                  </MetricBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MetricBox>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#43e97b', mb: 1 }}>
                      ${Math.round(totalProjectedData.average_cpm / creatorsReachProjections.length).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                      Average CPM
                    </Typography>
                  </MetricBox>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3, background: 'linear-gradient(90deg, transparent, #667eea, transparent)' }} />
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Chip 
                  label={`Top Age: ${totalProjectedData.top_audience} (${Math.round(totalProjectedData.projected_top_audience).toLocaleString()} views)`}
                  sx={{ 
                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
                <Chip 
                  label={`Top Gender: ${totalProjectedData.top_gender} (${totalProjectedData.projected_top_gender.toLocaleString()} views)`}
                  sx={{ 
                    background: 'linear-gradient(135deg, #f093fb, #f5576c)',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
            </StyledCardContent>
          </ModernCard>
        </Grid>

        {/* Country Distribution */}
        <Grid item xs={12} lg={6}>
          <ModernCard elevation={0}>
            <StyledCardHeader title="🌍 Geographic Distribution" />
            <StyledCardContent>
              <ChartContainer>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={totalProjectedData.country_data} layout="vertical">
                    <XAxis type="number" axisLine={false} tickLine={false} />
                    <YAxis dataKey="code" type="category" axisLine={false} tickLine={false} />
                    <Bar 
                      dataKey="value" 
                      fill="url(#countryGradient)" 
                      radius={[0, 8, 8, 0]}
                    >
                      <LabelList 
                        dataKey="value" 
                        position="right" 
                        formatter={(value) => `${value.toFixed(0)}%`}
                        style={{ fill: '#667eea', fontWeight: 600 }}
                      />
                    </Bar>
                    <Tooltip content={<CustomTooltip />} />
                    <defs>
                      <linearGradient id="countryGradient" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#667eea" />
                        <stop offset="100%" stopColor="#764ba2" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </StyledCardContent>
          </ModernCard>
        </Grid>

        {/* Gender Distribution */}
        <Grid item xs={12} md={6}>
          <ModernCard elevation={0}>
            <StyledCardHeader title="👥 Gender Demographics" />
            <StyledCardContent>
              <ChartContainer>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={totalProjectedData.gender_data}
                      cx="50%"
                      cy="50%"
                      dataKey="value"
                      nameKey="gender"
                      outerRadius="70%"
                      label={({ value }) => `${value.toFixed(1)}%`}
                      labelLine={false}
                    >
                      {totalProjectedData.gender_data.map((entry, index) => (
                        <Cell key={index} fill={genderColors[index % genderColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            </StyledCardContent>
          </ModernCard>
        </Grid>

        {/* Creator Reach Projections */}
        <Grid item xs={12} md={6}>
          <ModernCard elevation={0}>
            <StyledCardHeader title="🎯 Creator Reach Analysis" />
            <StyledCardContent>
              <ChartContainer>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={creatorsReachProjections}>
                    <XAxis dataKey="id" tick={false} axisLine={false} />
                    <YAxis axisLine={false} tickLine={false} />
                    <Bar dataKey="average_views" radius={[4, 4, 0, 0]}>
                      {creatorsReachProjections.map((_, index) => (
                        <Cell key={`views-cell-${index}`} fill={modernColors[index % modernColors.length]} />
                      ))}
                    </Bar>
                    <Tooltip content={<CustomTooltip />} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </StyledCardContent>
          </ModernCard>
        </Grid>

        {/* CPM Analysis */}
        <Grid item xs={12}>
          <ModernCard elevation={0}>
            <StyledCardHeader title="💰 Cost Performance Analysis" />
            <StyledCardContent>
              <ChartContainer>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={creatorsReachProjections}>
                    <XAxis dataKey="id" tick={false} axisLine={false} />
                    <YAxis axisLine={false} tickLine={false} />
                    <Bar dataKey="cpm" radius={[4, 4, 0, 0]}>
                      {creatorsReachProjections.map((_, index) => (
                        <Cell key={`cpm-cell-${index}`} fill={modernColors[index % modernColors.length]} />
                      ))}
                    </Bar>
                    <Tooltip content={<CustomTooltip />} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </StyledCardContent>
          </ModernCard>
        </Grid>
      </Grid>
    </StyledContainer>
  );
};

export default CampaignProjectionsPanel;